// src/pages/Checkout.js - Updated for Pan-India Delivery
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  MapPin,
  User,
  Package,
  ArrowLeft,
  Lock,
  CheckCircle,
  Tag,
  X,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  userAPI,
  ordersAPI,
  pincodeAPI,
  promoCodeAPI,
  authAPI,
} from "../services/api";
import { useRazorpay } from "../hooks/useRazorpay";
import Button from "../components/common/Button";
import Loading from "../components/common/Loading";
import BackgroundWrapper from "../components/common/BackgroundWrapper";
import PincodeInput from "../components/common/PincodeInput";
import ServiceAreaNotice from "../components/common/ServiceAreaNotice";
import PaymentProcessing from "../components/common/PaymentProcessing";
import toast from "react-hot-toast";
import "./Checkout.css";

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, items, cartTotal, clearCart, loading: cartLoading } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { processPayment, loading: paymentLoading } = useRazorpay();

  const actualCart = items || [];

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userAddresses, setUserAddresses] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(null);
  const [pincodeAutofilled, setPincodeAutofilled] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [showProcessingScreen, setShowProcessingScreen] = useState(false);

  const [paymentSettings, setPaymentSettings] = useState({
    razorpayEnabled: true,
  });

  useEffect(() => {
    window.__showProcessingScreen = () => {
      setShowProcessingScreen(true);
    };
    return () => {
      window.__showProcessingScreen = null;
    };
  }, []);

  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(false);

  // Form data - NO default state restriction
  const [shippingData, setShippingData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    state: "", // ✅ No default state
    zipCode: "",
    country: "India",
  });

  const [paymentData, setPaymentData] = useState({
    method: "razorpay",
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/checkout" } });
      return;
    }

    if (!actualCart || !Array.isArray(actualCart) || actualCart.length === 0) {
      navigate("/cart");
      return;
    }

    loadUserData();
  }, [isAuthenticated, actualCart]);

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const response = await fetch(
        "http://localhost:3001/api/payment-settings"
      );
      const data = await response.json();

      if (data.success) {
        setPaymentSettings({
          razorpayEnabled: data.data.razorpayEnabled,
        });

        if (!data.data.razorpayEnabled) {
          toast.error("Online payments are currently unavailable");
        }
      }
    } catch (error) {
      console.error("Failed to load payment settings:", error);
      setPaymentSettings({
        razorpayEnabled: true,
      });
    }
  };

  const loadUserData = async () => {
    try {
      setLoading(true);

      const response = await authAPI.getProfile();
      if (
        response.data.success &&
        response.data.user &&
        response.data.user.addresses
      ) {
        // ✅ Accept ALL addresses (not just Kerala)
        setUserAddresses(response.data.user.addresses);

        const defaultAddress = response.data.user.addresses.find(
          (addr) => addr.isDefault
        );
        if (defaultAddress) {
          setShippingData({
            firstName: defaultAddress.firstName || user?.firstName || "",
            lastName: defaultAddress.lastName || user?.lastName || "",
            email: user?.email || "",
            phone: defaultAddress.phone || "",
            address: defaultAddress.addressLine1 || "",
            city: defaultAddress.city || "",
            state: defaultAddress.state || "", // ✅ Use actual state
            zipCode: defaultAddress.postalCode || "",
            country: "India",
          });

          // Fetch delivery info for default address
          if (defaultAddress.postalCode) {
            await handlePincodeAutofill(defaultAddress.postalCode);
          }
        }
      }
    } catch (error) {
      console.error("Failed to load user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    try {
      setPromoLoading(true);

      const { subtotal } = calculateTotals();

      const itemsForValidation = actualCart.map((item) => ({
        productId: item.product?._id,
        categoryId: item.product?.category,
        quantity: item.quantity,
      }));

      const response = await promoCodeAPI.validatePromoCode({
        code: promoCode.trim().toUpperCase(),
        orderAmount: subtotal,
        items: itemsForValidation,
      });

      if (response.data.success) {
        setAppliedPromo({
          code: response.data.promoCode.code,
          description: response.data.promoCode.description,
          discountAmount: response.data.discount.amount,
          discountType: response.data.discount.type,
        });

        toast.success(`🎉 ${response.data.message}`);
        setShowPromoInput(false);
      }
    } catch (error) {
      console.error("Promo code error:", error);

      const errorMessage =
        error.response?.data?.message || "Invalid promo code";

      if (error.response?.data?.minOrderAmount) {
        toast.error(
          `${errorMessage} (Min: ₹${error.response.data.minOrderAmount})`
        );
      } else {
        toast.error(errorMessage);
      }

      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    toast.success("Promo code removed");
  };

  const handleAddressSelection = async (address, index) => {
    if (address === null || index === null) {
      setSelectedAddressIndex(null);
      setShippingData({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        phone: "",
        address: "",
        city: "",
        state: "", // ✅ No default
        zipCode: "",
        country: "India",
      });
      setPincodeAutofilled(false);
      setDeliveryInfo(null);
      return;
    }

    if (selectedAddressIndex === index) {
      setSelectedAddressIndex(null);
      setShippingData({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        country: "India",
      });
      setPincodeAutofilled(false);
      setDeliveryInfo(null);
    } else {
      setSelectedAddressIndex(index);
      setShippingData({
        firstName: address.firstName || user?.firstName || "",
        lastName: address.lastName || user?.lastName || "",
        email: user?.email || "",
        phone: address.phone || "",
        address: address.addressLine1 || address?.address || "",
        city: address.city || "",
        state: address.state || "", // ✅ Use actual state
        zipCode: address.postalCode || address?.zipCode || "",
        country: "India",
      });
      setPincodeAutofilled(true);

      // Fetch delivery info
      if (address.postalCode) {
        try {
          const pinRes = await pincodeAPI.validatePincode(address.postalCode);
          const deliveryRes = await pincodeAPI.getDeliveryInfo(
            address.postalCode
          );

          if (pinRes.data?.success) {
            handlePincodeAutofill({
              pincode: address.postalCode,
              city: pinRes.data.data.district || pinRes.data.data.city,
              state: pinRes.data.data.state,
              deliveryCharge: deliveryRes.data?.deliveryCharge ?? null,
              estimatedDays: deliveryRes.data?.estimatedDays ?? 3,
            });
          }
        } catch (err) {
          console.error("Failed to fetch delivery info:", err);
          toast.error("Failed to fetch delivery details for this address");
        }
      }
    }
  };

  const handlePincodeAutofill = async (locationData) => {
    if (!locationData) return;

    setShippingData((prev) => ({
      ...prev,
      city: locationData.city,
      state: locationData.state,
      zipCode: locationData.pincode || prev.zipCode,
    }));

    const rawCharge =
      locationData.deliveryCharge ?? locationData.charge ?? null;

    const normalizedCharge =
      rawCharge === null
        ? null
        : rawCharge === 0 || rawCharge === "Free"
        ? 0
        : Number(rawCharge);

    setDeliveryInfo({
      deliveryCharge: normalizedCharge,
      state: locationData.state,
      estimatedDays: locationData.estimatedDays,
    });

    setPincodeAutofilled(true);

    const chargeText =
      normalizedCharge === 0
        ? "Free delivery"
        : `₹${normalizedCharge} delivery charge`;

    toast.success(`${chargeText} to ${locationData.state}`);
  };

  const calculatedSubtotal = React.useMemo(() => {
    if (!Array.isArray(actualCart)) return 0;

    return actualCart.reduce((sum, item) => {
      const price = item.product?.salePrice ?? item.product?.price ?? 0;

      return sum + price * (item.quantity || 1);
    }, 0);
  }, [actualCart]);

  const calculateTotals = () => {
    const subtotal = calculatedSubtotal;
    const discount = appliedPromo?.discountAmount || 0;
    const subtotalAfterDiscount = Math.max(0, subtotal - discount);

    // ✅ SINGLE SOURCE OF TRUTH: backend
    const shipping = deliveryInfo?.deliveryCharge ?? null;

    if (!deliveryInfo) {
      return {
        subtotal,
        shipping: null,
        tax: 0,
        discount,
        total: subtotal,
      };
    }

    const tax = Math.round(subtotalAfterDiscount * 0.18);

    return {
      subtotal,
      shipping,
      tax,
      discount,
      total: subtotalAfterDiscount + shipping + tax,
    };
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();

    if (selectedAddressIndex !== null) {
      setCurrentStep(2);
      return;
    }

    // Validate the manual form
    const required = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "address",
      "city",
      "state", // ✅ Now required
      "zipCode",
    ];
    const missing = required.filter((field) => !shippingData[field]?.trim());

    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    // Verify address with backend
    try {
      const verifyResponse = await pincodeAPI.verifyAddress({
        pincode: shippingData.zipCode,
        city: shippingData.city,
        state: shippingData.state, // ✅ Verify actual state
      });

      if (verifyResponse.data.suggestions) {
        const confirmMessage =
          `Address mismatch detected!\n\n` +
          `Your entered: ${shippingData.city}, ${shippingData.state}\n` +
          `Suggested: ${verifyResponse.data.suggestions.correctCity}, ${verifyResponse.data.suggestions.correctState}\n\n` +
          `Do you want to continue?`;

        if (!window.confirm(confirmMessage)) {
          return;
        }
      }
    } catch (error) {
      console.warn("Address verification failed:", error);
      toast.error("Unable to verify PIN code. Please ensure it's valid.");
    }

    setCurrentStep(2);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setCurrentStep(3);
  };

  const placeOrder = async () => {
    try {
      setProcessing(true);

      // Final address verification
      try {
        const verificationResponse = await pincodeAPI.verifyAddress({
          pincode: shippingData.zipCode,
          city: shippingData.city,
          state: shippingData.state,
        });

        if (!verificationResponse.data.success) {
          toast.error("Invalid delivery address");
          setProcessing(false);
          return;
        }
      } catch (verificationError) {
        console.warn("Address verification failed:", verificationError);
      }

      const { subtotal, shipping, tax, discount, total } = calculateTotals();

      const orderData = {
        items: actualCart.map((item) => {
          const productId = item.product?._id || item.productId || item.product;
          const itemSize = item.size || item.selectedSize || item.variant?.size;
          const itemColor =
            item.color ||
            item.selectedColor ||
            item.variant?.color?.name ||
            item.variant?.color;

          return {
            product: productId,
            quantity: item.quantity || 1,
            size: itemSize,
            color: itemColor,
            price: item.product?.salePrice || item.product?.price || 0,
          };
        }),
        shippingAddress: {
          firstName: shippingData.firstName,
          lastName: shippingData.lastName,
          email: shippingData.email,
          phone: shippingData.phone,
          addressLine1: shippingData.address,
          city: shippingData.city,
          state: shippingData.state, // ✅ Actual state
          postalCode: shippingData.zipCode,
          country: "India",
        },
        billingAddress: {
          firstName: shippingData.firstName,
          lastName: shippingData.lastName,
          email: shippingData.email,
          phone: shippingData.phone,
          addressLine1: shippingData.address,
          city: shippingData.city,
          state: shippingData.state, // ✅ Actual state
          postalCode: shippingData.zipCode,
          country: "India",
        },
        paymentMethod: paymentData.method,
        shippingMethod: "standard",
        subtotal,
        shipping,
        tax,
        total,
        discount,
        promoCode: appliedPromo?.code || null,
      };

      if (paymentData.method === "razorpay") {
        await processPayment(
          orderData,
          (order) => {
            toast.success("🎉 Order placed successfully!");
            clearCart().catch((err) => console.warn("Cart clear error:", err));
            navigate(`/order-success/${order._id}`, { replace: true });
          },
          (error) => {
            console.error("❌ Payment failed:", error);
            setProcessing(false);
          }
        );
      }
    } catch (error) {
      console.error("❌ Order placement error:", error);

      // Timeout handling (keep this as-is)
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        toast.error(
          "Request timeout. Your order may have been created. Please check your orders page.",
          { duration: 5000 }
        );
        setTimeout(() => {
          navigate("/account/orders", { replace: true });
        }, 2000);
        return;
      }

      const data = error.response?.data;

      // ✅ KERALA-ONLY → WhatsApp redirect / CTA
      if (data?.whatsappLink) {
        toast.custom(() => (
          <div style={{ padding: "12px", maxWidth: "320px" }}>
            <p style={{ marginBottom: "10px" }}>{data.message}</p>
            <a
              href={data.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                padding: "10px 14px",
                background: "#25D366",
                color: "#fff",
                borderRadius: "6px",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              💬 Order via WhatsApp
            </a>
          </div>
        ));
        return;
      }

      // Fallback
      toast.error(data?.message || "Failed to place order. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading || cartLoading) {
    return <Loading size="lg" text="Loading checkout..." fullScreen />;
  }

  if (showProcessingScreen) {
    return <PaymentProcessing />;
  }

  // ✅ NOW it is safe to calculate totals
  const totals = calculateTotals();

  return (
    <BackgroundWrapper>
      <div className="checkout-page">
        <div className="container">
          {/* Header */}
          <div className="checkout-header">
            <button onClick={() => navigate(-1)} className="back-btn">
              <ArrowLeft size={20} />
              Back
            </button>
            <h1>Checkout</h1>
            <div className="step-indicator">
              <span className={currentStep >= 1 ? "active" : ""}>1</span>
              <span className={currentStep >= 2 ? "active" : ""}>2</span>
              <span className={currentStep >= 3 ? "active" : ""}>3</span>
            </div>
          </div>

          <div className="checkout-content">
            {/* Main Content */}
            <div className="checkout-main">
              {/* Step 1: Shipping Information */}
              {currentStep === 1 && (
                <div className="checkout-step">
                  <div className="step-header">
                    <MapPin size={24} />
                    <h2>Shipping Information</h2>
                  </div>

                  <ServiceAreaNotice variant="info" />

                  {userAddresses && userAddresses.length > 0 && (
                    <div className="saved-addresses">
                      <h3>Use Saved Address</h3>
                      <div className="address-options">
                        {userAddresses.map((address, index) => (
                          <div
                            key={index}
                            className={`address-option ${
                              selectedAddressIndex === index ? "selected" : ""
                            }`}
                          >
                            <label>
                              <input
                                type="radio"
                                name="savedAddress"
                                checked={selectedAddressIndex === index}
                                onChange={() =>
                                  handleAddressSelection(address, index)
                                }
                              />
                              <div className="address-display">
                                <p>
                                  <strong>
                                    {address.firstName} {address.lastName}
                                  </strong>
                                </p>
                                <p>{address.addressLine1}</p>
                                {address.addressLine2 && (
                                  <p>{address.addressLine2}</p>
                                )}
                                <p>
                                  {address.city}, {address.state}{" "}
                                  {address.postalCode}
                                </p>
                                <p>{address.phone}</p>
                                {address.isDefault && (
                                  <span className="default-badge">Default</span>
                                )}
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>

                      {selectedAddressIndex !== null && (
                        <div className="address-actions">
                          <button
                            type="button"
                            className="clear-selection-btn"
                            onClick={() => handleAddressSelection(null, null)}
                          >
                            Clear Selection & Enter New Address
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedAddressIndex === null && (
                    <>
                      <div className="form-divider">
                        <span>Enter New Address</span>
                      </div>

                      <form
                        onSubmit={handleShippingSubmit}
                        className="checkout-form"
                      >
                        <div className="form-row">
                          <div className="form-group">
                            <label>First Name *</label>
                            <input
                              type="text"
                              value={shippingData.firstName}
                              onChange={(e) =>
                                setShippingData({
                                  ...shippingData,
                                  firstName: e.target.value,
                                })
                              }
                              placeholder="Enter first name"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Last Name *</label>
                            <input
                              type="text"
                              value={shippingData.lastName}
                              onChange={(e) =>
                                setShippingData({
                                  ...shippingData,
                                  lastName: e.target.value,
                                })
                              }
                              placeholder="Enter last name"
                              required
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Email *</label>
                            <input
                              type="email"
                              value={shippingData.email}
                              onChange={(e) =>
                                setShippingData({
                                  ...shippingData,
                                  email: e.target.value,
                                })
                              }
                              placeholder="your@email.com"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Phone *</label>
                            <input
                              type="tel"
                              value={shippingData.phone}
                              onChange={(e) =>
                                setShippingData({
                                  ...shippingData,
                                  phone: e.target.value,
                                })
                              }
                              placeholder="+91 9876543210"
                              required
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Street Address *</label>
                          <input
                            type="text"
                            value={shippingData.address}
                            onChange={(e) =>
                              setShippingData({
                                ...shippingData,
                                address: e.target.value,
                              })
                            }
                            placeholder="House number, street name, area"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>PIN Code *</label>
                          <PincodeInput
                            value={shippingData.zipCode}
                            onChange={(value) => {
                              setShippingData((prev) => ({
                                ...prev,
                                zipCode: value,
                              }));

                              // only reset if PIN becomes invalid
                              if (!/^[1-9][0-9]{5}$/.test(value)) {
                                setPincodeAutofilled(false);
                                setDeliveryInfo(null);
                              }
                            }}
                            onAutofill={handlePincodeAutofill}
                            showDeliveryInfo={true}
                            required
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>City *</label>
                            {pincodeAutofilled ? (
                              <div className="autofilled-field">
                                <input
                                  type="text"
                                  value={shippingData.city}
                                  disabled
                                  className="autofilled-input"
                                />
                                <small className="autofilled-label">
                                  ✓ Auto-filled from PIN code
                                </small>
                              </div>
                            ) : (
                              <input
                                type="text"
                                value={shippingData.city}
                                onChange={(e) =>
                                  setShippingData({
                                    ...shippingData,
                                    city: e.target.value,
                                  })
                                }
                                placeholder="Enter city"
                                required
                              />
                            )}
                          </div>

                          <div className="form-group">
                            <label>State *</label>
                            {pincodeAutofilled ? (
                              <div className="autofilled-field">
                                <input
                                  type="text"
                                  value={shippingData.state}
                                  disabled
                                  className="autofilled-input"
                                />
                                <small className="autofilled-label">
                                  ✓ Auto-filled from PIN code
                                </small>
                              </div>
                            ) : (
                              <input
                                type="text"
                                value={shippingData.state}
                                onChange={(e) =>
                                  setShippingData({
                                    ...shippingData,
                                    state: e.target.value,
                                  })
                                }
                                placeholder="Enter state"
                                required
                              />
                            )}
                          </div>

                          <div className="form-group">
                            <label>Country</label>
                            <input
                              type="text"
                              value="India"
                              disabled
                              className="disabled-input"
                            />
                          </div>
                        </div>

                        <Button type="submit" className="continue-btn">
                          Continue to Payment
                        </Button>
                      </form>
                    </>
                  )}

                  {selectedAddressIndex !== null && (
                    <div className="selected-address-actions">
                      <Button
                        type="button"
                        className="continue-btn"
                        onClick={() => setCurrentStep(2)}
                      >
                        Continue with Selected Address
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Payment Information */}
              {currentStep === 2 && (
                <div className="checkout-step">
                  <div className="step-header">
                    <CreditCard size={24} />
                    <h2>Payment Information</h2>
                  </div>

                  <form
                    onSubmit={handlePaymentSubmit}
                    className="checkout-form"
                  >
                    <div className="payment-methods">
                      {/* ✅ Only show Razorpay if enabled */}
                      {paymentSettings.razorpayEnabled && (
                        <label className="payment-method">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="razorpay"
                            checked={paymentData.method === "razorpay"}
                            onChange={(e) =>
                              setPaymentData({
                                ...paymentData,
                                method: e.target.value,
                              })
                            }
                          />
                          <CreditCard size={20} />
                          Pay Online (Cards/UPI/Netbanking)
                        </label>
                      )}

                      {!paymentSettings.razorpayEnabled && (
                        <div className="alert warning">
                          <strong>⚠️ Payments unavailable</strong>
                          <p>Please try again later.</p>
                        </div>
                      )}
                    </div>

                    {paymentData.method === "razorpay" && (
                      <div
                        className="payment-info"
                        style={{
                          padding: "20px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          marginTop: "20px",
                          marginBottom: "20px",
                        }}
                      >
                        <h4 style={{ marginBottom: "10px" }}>
                          Secure Payment via Razorpay
                        </h4>
                        <p style={{ marginBottom: "15px", color: "#666" }}>
                          After clicking "Review Order", you'll be able to
                          choose from multiple payment options including
                          credit/debit cards, UPI, net banking, and wallets.
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: "15px",
                            flexWrap: "wrap",
                            fontSize: "14px",
                            color: "#444",
                          }}
                        >
                          <span>💳 Credit/Debit Cards</span>
                          <span>📱 UPI</span>
                          <span>🏦 Net Banking</span>
                          <span>💰 Wallets</span>
                        </div>

                        {/* Cancellation Policy Notice */}
                        <div
                          style={{
                            marginTop: "15px",
                            padding: "12px",
                            background: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: "6px",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: "13px",
                              color: "#856404",
                              lineHeight: "1.5",
                            }}
                          >
                            <strong>⏰ Cancellation Policy:</strong> You can
                            cancel this order within <strong>24 hours</strong>{" "}
                            of placement for an automatic refund (5-7 business
                            days). After 24 hours, you can still return the
                            product within 3 days of delivery.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="checkout-actions">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCurrentStep(1)}
                      >
                        Back to Shipping
                      </Button>
                      <Button type="submit" className="continue-btn">
                        Review Order
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 3: Order Review */}
              {currentStep === 3 && (
                <div className="checkout-step">
                  <div className="step-header">
                    <CheckCircle size={24} />
                    <h2>Review Your Order</h2>
                  </div>

                  <div className="order-review">
                    {/* Shipping Address */}
                    <div className="review-section">
                      <h3>Shipping Address</h3>
                      <div className="address-display">
                        <p>
                          <strong>
                            {shippingData.firstName} {shippingData.lastName}
                          </strong>
                          <br />
                          {shippingData.address}
                        </p>
                        <p>
                          {shippingData.city}, {shippingData.state}{" "}
                          {shippingData.zipCode}
                        </p>

                        <p>India</p>
                        <p>{shippingData.phone}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Payment Method */}
                    <div className="review-section">
                      <h3>Payment Method</h3>
                      <p>Pay Online (Cards / UPI / Netbanking)</p>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                    </div>

                    {/* ✅ NEW: Promo Code Section */}
                    <div className="review-section promo-section">
                      <h3>
                        <Tag size={20} />
                        Have a Promo Code?
                      </h3>

                      {!appliedPromo ? (
                        <>
                          {!showPromoInput ? (
                            <button
                              type="button"
                              className="promo-toggle-btn"
                              onClick={() => setShowPromoInput(true)}
                            >
                              + Add Promo Code
                            </button>
                          ) : (
                            <div className="promo-input-group">
                              <input
                                type="text"
                                value={promoCode}
                                onChange={(e) =>
                                  setPromoCode(e.target.value.toUpperCase())
                                }
                                placeholder="Enter promo code"
                                className="promo-input"
                                disabled={promoLoading}
                              />
                              <Button
                                onClick={handleApplyPromo}
                                loading={promoLoading}
                                disabled={!promoCode.trim() || promoLoading}
                                size="sm"
                              >
                                Apply
                              </Button>
                              <button
                                type="button"
                                className="promo-cancel-btn"
                                onClick={() => {
                                  setShowPromoInput(false);
                                  setPromoCode("");
                                }}
                                disabled={promoLoading}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="applied-promo">
                          <div className="promo-info">
                            <Tag size={18} className="promo-icon" />
                            <div>
                              <p className="promo-code">{appliedPromo.code}</p>
                              <p className="promo-description">
                                {appliedPromo.description}
                              </p>
                              <p className="promo-savings">
                                You saved ₹
                                {appliedPromo.discountAmount.toFixed(2)}!
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-promo-btn"
                            onClick={handleRemovePromo}
                          >
                            <X size={18} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Cancellation Policy Reminder */}
                    <div
                      style={{
                        padding: "15px",
                        background: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "8px",
                        marginTop: "20px",
                        marginBottom: "20px",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#856404",
                          lineHeight: "1.6",
                        }}
                      >
                        <strong>📋 Cancellation Policy:</strong> By placing this
                        order, you acknowledge that you can cancel within{" "}
                        <strong>24 hours</strong> of order placement for an
                        automatic refund. After 24 hours, you may return the
                        product within 30 days of delivery.
                      </p>
                    </div>

                    <div className="final-actions">
                      <Button
                        onClick={placeOrder}
                        loading={processing}
                        disabled={processing}
                        className="place-order-btn"
                      >
                        <Lock size={18} />
                        {processing
                          ? "Processing..."
                          : `Place Order - ₹${totals.total.toFixed(2)}`}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="order-summary">
              <h3>Order Summary</h3>

              <div className="cart-items">
                {actualCart &&
                Array.isArray(actualCart) &&
                actualCart.length > 0 ? (
                  actualCart.map((item, index) => (
                    <div
                      key={`${item.product?._id || index}-${item.size}-${
                        item.color
                      }`}
                      className="cart-item"
                    >
                      <img
                        src={
                          item.product?.images?.[0]?.url ||
                          "/placeholder-image.jpg"
                        }
                        alt={item.product?.name || "Product"}
                        onError={(e) => {
                          e.target.src = "/placeholder-image.jpg";
                        }}
                      />
                      <div className="item-details">
                        <h4>{item.product?.name || "Product"}</h4>
                        <p>
                          Size:{" "}
                          {item.size ||
                            item.selectedSize ||
                            item.variant?.size ||
                            "N/A"}{" "}
                          | Color:{" "}
                          {item.color ||
                            item.selectedColor ||
                            item.variant?.color?.name ||
                            item.variant?.color ||
                            "N/A"}
                        </p>
                        <p>Qty: {item.quantity}</p>
                      </div>
                      <div className="item-price">
                        ₹
                        {(
                          (item.product?.salePrice ||
                            item.product?.price ||
                            0) * item.quantity
                        ).toFixed(2)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-cart">
                    <p>Your cart is empty</p>
                  </div>
                )}
              </div>

              <div className="order-totals">
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>₹{totals.subtotal.toFixed(2)}</span>
                </div>

                {/* ✅ NEW: Show discount if applied */}
                {appliedPromo && (
                  <div className="total-row discount-row">
                    <span>
                      <Tag size={14} />
                      Discount ({appliedPromo.code}):
                    </span>
                    <span className="discount-amount">
                      -₹{totals.discount.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="total-row">
                  <span>Shipping:</span>
                  <span>
                    {totals.shipping === null
                      ? "Enter PIN"
                      : totals.shipping === 0
                      ? "Free"
                      : `₹${totals.shipping}`}
                  </span>
                </div>
                <div className="total-row">
                  <span>Tax (GST):</span>
                  <span>₹{totals.tax.toFixed(2)}</span>
                </div>
                <div className="total-row final-total">
                  <span>Total:</span>
                  <span>₹{totals.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Kerala Delivery Notice */}
              <div className="delivery-notice">
                <MapPin size={16} />
                <div>
                  <strong>
                    {deliveryInfo?.state
                      ? `Delivery to ${deliveryInfo.state}`
                      : "Delivery to India"}
                  </strong>
                  <p>
                    {deliveryInfo
                      ? `Estimated: ${deliveryInfo.estimatedDays} days`
                      : "Enter PIN to see delivery time"}
                  </p>
                </div>
              </div>

              <div className="security-notice">
                <Lock size={16} />
                <span>Your payment information is secure and encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BackgroundWrapper>
  );
};

export default Checkout;
